import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectAuthUser,
  clearBiometricEnrollmentPrompt,
} from '../../redux/features/userAuth/userAuthSlice'
import { enrollBiometricLogin } from '../../redux/features/userAuth/biometricThunk'
import BiometricAuthService from '../../services/BiometricAuthService'
import {
  consumePendingBiometricCredentials,
  clearPendingBiometricCredentials,
} from '../../services/PendingBiometricCredentials'
import UseTranslation from '../../hooks/UseTranslation'
import EnableBiometricPrompt from './EnableBiometricPrompt'

/**
 * Offers biometric enrollment after a fresh password login.
 *
 * This lives at the app root rather than on the login page on purpose: the
 * login route is wrapped in `PublicAuthRoute`, which redirects away during
 * render as soon as a session cookie exists. Anything rendered inside the
 * login page is therefore unmounted before it can show a dialog. Mounted
 * here, the prompt survives that redirect and appears over whichever screen
 * the user lands on.
 */
const BiometricEnrollmentGate = () => {
  const dispatch = useDispatch()
  const { t } = UseTranslation()
  const { promptBiometricEnrollment } = useSelector(selectAuthUser)

  const [open, setOpen] = useState(false)
  const [biometryType, setBiometryType] = useState(null)
  const [loading, setLoading] = useState(false)
  const [credentials, setCredentials] = useState(null)

  useEffect(() => {
    if (!promptBiometricEnrollment) return undefined

    let cancelled = false

    async function offerEnrollment() {
      // Read the handoff immediately so the credentials are not left sitting in
      // memory if any of the checks below bail out.
      const pending = consumePendingBiometricCredentials()

      try {
        const [{ available, type }, vault] = await Promise.all([
          BiometricAuthService.isAvailable(),
          BiometricAuthService.getVaultStatus(),
        ])

        // Offer enrollment when nothing is saved yet, and also when the saved
        // sign-in belongs to a different account - otherwise a second user on
        // the device would never be offered it, and the biometric button would
        // keep signing in as the first one.
        const alreadyEnrolled = vault.enrolled &&
          vault.email?.toLowerCase() === pending?.email?.toLowerCase()

        // `type` is required: only Face ID and fingerprint are offered, so a
        // device reporting some other biometry never reaches the dialog.
        if (!cancelled && available && type && !alreadyEnrolled &&
          pending?.email && pending?.password) {
          setCredentials(pending)
          setBiometryType(type)
          setOpen(true)
        }
      } catch (e) {
        // device cannot report biometry; silently skip the offer
      } finally {
        if (!cancelled) {
          dispatch(clearBiometricEnrollmentPrompt())
        }
      }
    }

    offerEnrollment()

    return () => {
      cancelled = true
      clearPendingBiometricCredentials()
    }
  }, [promptBiometricEnrollment, dispatch])

  const closeAndForget = () => {
    setOpen(false)
    setCredentials(null)
  }

  // Declining leaves the vault exactly as it was. With nothing stored yet that
  // means no credentials are saved and the login screen shows no biometric
  // button; if an earlier account is already enrolled it stays enrolled, so the
  // button keeps signing in as that account until someone accepts a new one.
  const handleAccept = async () => {
    setLoading(true)
    await dispatch(
      enrollBiometricLogin({
        credentials,
        reason: t?.['Enable biometric login'] || 'Enable biometric login',
      }),
    )
    setLoading(false)
    closeAndForget()
  }

  const handleDecline = () => {
    closeAndForget()
  }

  return (
    <EnableBiometricPrompt
      open={open}
      type={biometryType}
      loading={loading}
      onAccept={handleAccept}
      onDecline={handleDecline}
      t={t}
    />
  )
}

export default BiometricEnrollmentGate
